import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { API } from "@/lib/api";
import { useRole } from "@/lib/roles";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Settings as SettingsIcon, ShieldCheck, Loader2, BarChart3, Download, ChevronLeft, ChevronRight } from "lucide-react";

export default function Settings() {
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data));
  }, []);

  const save = async (patch) => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      const next = { ...settings, ...patch };
      setSettings(next); // optimistic
      const { data } = await api.put("/settings", next);
      setSettings(data);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save");
      // refetch on error
      const { data } = await api.get("/settings");
      setSettings(data);
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="p-10 flex items-center gap-3 text-[#50493b]">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading settings…
      </div>
    );
  }

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="mb-2 text-[11px] subtitle tracking-[0.3em] uppercase text-[#39678f]">
        Administration
      </div>
      <h1 className="font-display text-5xl tracking-tight text-[#33424f] leading-none mb-3">
        Settings
      </h1>
      <p className="font-body text-sm text-[#50493b] mb-8 max-w-xl">
        Registry-wide preferences. Changes take effect immediately across every
        user session. Only administrators can edit.
      </p>

      {!isAdmin && (
        <div
          data-testid="settings-read-only-banner"
          className="mb-6 border border-[#ccb795] bg-[#f6efe2] px-4 py-3 text-[12px] subtitle uppercase tracking-[0.2em] text-[#50493b] flex items-center gap-2"
        >
          <ShieldCheck className="w-4 h-4" />
          Read-only view · sign in as admin to modify
        </div>
      )}

      {/* Sections */}
      <div className="space-y-6">
        <SettingRow
          icon={SettingsIcon}
          title="Auto-assign vessel number"
          description="When enabled, new pressure tests get the next sequential vessel number within their Job + Vessel Type automatically. Welders can still override by typing a number in the form before saving. Turn this off if your team often logs tests out-of-order."
          value={settings.auto_vessel_number}
          onChange={(v) => save({ auto_vessel_number: v })}
          disabled={!isAdmin || saving}
          testid="setting-auto-vessel-number"
        />

        <NumberSettingRow
          icon={BarChart3}
          title="Dashboard analytics window"
          description="How many trailing months the Pass-Rate Trend chart and PTF Heatmap on the Dashboard cover. Range 1–12."
          value={Number(settings.analytics_months) || 6}
          onChange={(v) => save({ analytics_months: v })}
          min={1}
          max={12}
          unit="months"
          disabled={!isAdmin || saving}
          testid="setting-analytics-months"
        />
      </div>

      {isAdmin && <AccessLogPanel />}

      <div className="mt-12 flex items-center gap-3">
        <Button
          data-testid="settings-back-btn"
          variant="outline"
          onClick={() => navigate(-1)}
          className="border-[#33424f]/30 text-[#33424f] hover:bg-[#e6e0db]"
        >
          Back
        </Button>
        {saving && (
          <span className="text-xs subtitle uppercase tracking-[0.2em] text-[#50493b] flex items-center gap-1.5">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…
          </span>
        )}
      </div>
    </div>
  );
}

function SettingRow({ icon: Icon, title, description, value, onChange, disabled, testid }) {
  return (
    <div className="border border-[#33424f]/15 bg-white p-6 flex items-start gap-4">
      <div className="h-10 w-10 shrink-0 border border-[#33424f]/20 bg-[#e6f3ff] flex items-center justify-center text-[#39678f]">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 pr-4">
        <div className="subtitle uppercase tracking-[0.2em] text-[11px] text-[#33424f]">{title}</div>
        <div className="font-body text-sm text-[#50493b] mt-2 leading-relaxed">{description}</div>
      </div>
      <Switch
        data-testid={testid}
        checked={!!value}
        onCheckedChange={onChange}
        disabled={disabled}
      />
    </div>
  );
}

function NumberSettingRow({ icon: Icon, title, description, value, onChange, min, max, unit, disabled, testid }) {
  return (
    <div className="border border-[#33424f]/15 bg-white p-6 flex items-start gap-4">
      <div className="h-10 w-10 shrink-0 border border-[#33424f]/20 bg-[#e6f3ff] flex items-center justify-center text-[#39678f]">
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 pr-4">
        <div className="subtitle uppercase tracking-[0.2em] text-[11px] text-[#33424f]">{title}</div>
        <div className="font-body text-sm text-[#50493b] mt-2 leading-relaxed">{description}</div>
      </div>
      <div className="flex items-center gap-2">
        <input
          data-testid={testid}
          type="number"
          min={min}
          max={max}
          value={value}
          disabled={disabled}
          onChange={(e) => {
            const n = Math.max(min, Math.min(max, parseInt(e.target.value, 10) || min));
            onChange(n);
          }}
          className="w-20 h-10 border border-[#33424f]/30 px-3 text-center subtitle text-lg text-[#33424f] focus:border-[#39678f] focus:outline-none disabled:opacity-50 disabled:bg-[#f5f1ec]"
        />
        <span className="subtitle text-[10px] uppercase tracking-[0.2em] text-[#50493b]">{unit}</span>
      </div>
    </div>
  );
}


const EVENT_LABEL = {
  oauth_login_started: "Google sign-in started",
  oauth_login_succeeded: "Google sign-in OK",
  oauth_login_failed: "Google sign-in failed",
  oauth_domain_rejected: "Domain rejected",
  internal_login_succeeded: "Internal login OK",
  internal_login_failed: "Internal login failed",
  logout: "Logout",
  session_expired: "Session expired",
  access_denied: "Access denied",
  admin_action: "Admin action",
  sensitive_tool_accessed: "Sensitive tool",
};

const PAGE_SIZE = 25;

function AccessLogPanel() {
  const [data, setData] = useState(null); // {items,total,page,pages}
  const [page, setPage] = useState(1);
  const [event, setEvent] = useState("all");
  const [success, setSuccess] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const buildParams = useCallback(
    (extra = {}) => {
      const p = { page, page_size: PAGE_SIZE, ...extra };
      if (event !== "all") p.event = event;
      if (success !== "all") p.success = success;
      if (dateFrom) p.date_from = dateFrom;
      if (dateTo) p.date_to = dateTo;
      if (search.trim()) p.search = search.trim();
      return p;
    },
    [page, event, success, dateFrom, dateTo, search]
  );

  // Reset to page 1 whenever a filter changes.
  useEffect(() => {
    queueMicrotask(() => setPage(1));
  }, [event, success, dateFrom, dateTo, search]);

  useEffect(() => {
    let active = true;
    const t = setTimeout(() => {
      setLoading(true);
      api
        .get("/security/auth-log", { params: buildParams() })
        .then((r) => active && setData(r.data))
        .catch(() => active && setData({ items: [], total: 0, page: 1, pages: 1 }))
        .finally(() => active && setLoading(false));
    }, 250);
    return () => {
      active = false;
      clearTimeout(t);
    };
  }, [buildParams]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem("jackstone_token");
      const qs = new URLSearchParams();
      if (event !== "all") qs.set("event", event);
      if (success !== "all") qs.set("success", success);
      if (dateFrom) qs.set("date_from", dateFrom);
      if (dateTo) qs.set("date_to", dateTo);
      if (search.trim()) qs.set("search", search.trim());
      const res = await fetch(`${API}/security/auth-log.csv?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
      });
      if (!res.ok) throw new Error("export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "jackstone_access_log.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export the log.");
    } finally {
      setExporting(false);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;
  const inputCls =
    "h-9 border border-[#33424f]/30 px-2 text-xs font-body bg-white focus:border-[#39678f] focus:outline-none";

  return (
    <div className="mt-10" data-testid="access-log-panel">
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#39678f]" />
          <h2 className="subtitle uppercase tracking-[0.2em] text-[12px] text-[#33424f]">
            Access &amp; Security Log
          </h2>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={exportCsv}
          disabled={exporting || total === 0}
          data-testid="export-access-log-btn"
          className="h-9 border-[#33424f]/30 text-[#33424f] subtitle uppercase tracking-wider text-[10px] hover:bg-[#f5f1ec] disabled:opacity-40"
        >
          {exporting ? (
            <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" />
          ) : (
            <Download className="w-3.5 h-3.5 mr-2" />
          )}
          Export CSV
        </Button>
      </div>
      <p className="font-body text-sm text-[#50493b] mb-4 max-w-xl">
        Authentication and access events (most recent first). Passwords and tokens
        are never recorded. Export respects the filters below.
      </p>

      {/* Filters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <select
          data-testid="access-log-event-filter"
          value={event}
          onChange={(e) => setEvent(e.target.value)}
          className={inputCls}
        >
          <option value="all">All events</option>
          {Object.keys(EVENT_LABEL).map((k) => (
            <option key={k} value={k}>
              {EVENT_LABEL[k]}
            </option>
          ))}
        </select>
        <select
          data-testid="access-log-result-filter"
          value={success}
          onChange={(e) => setSuccess(e.target.value)}
          className={inputCls}
        >
          <option value="all">Any result</option>
          <option value="true">Success</option>
          <option value="false">Failure</option>
        </select>
        <input
          data-testid="access-log-date-from"
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className={inputCls}
        />
        <input
          data-testid="access-log-date-to"
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className={inputCls}
        />
        <input
          data-testid="access-log-search"
          type="text"
          placeholder="Email or IP…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={inputCls}
        />
      </div>

      <div className="border border-[#33424f]/15 bg-white overflow-x-auto">
        <table className="w-full text-xs data-table min-w-[720px]">
          <thead>
            <tr>
              <th className="text-left px-3 py-2">Time</th>
              <th className="text-left px-3 py-2">Event</th>
              <th className="text-left px-3 py-2">Identity</th>
              <th className="text-left px-3 py-2">Role</th>
              <th className="text-left px-3 py-2">IP</th>
              <th className="text-left px-3 py-2">Result</th>
            </tr>
          </thead>
          <tbody>
            {loading && data === null ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[#50493b]">
                  Loading…
                </td>
              </tr>
            ) : items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[#50493b]">
                  No access events match the current filters.
                </td>
              </tr>
            ) : (
              items.map((l) => (
                <tr key={l.id} className="border-t border-[#33424f]/10">
                  <td className="px-3 py-2 whitespace-nowrap text-[#50493b]">{fmt(l.timestamp)}</td>
                  <td className="px-3 py-2 subtitle text-[#33424f]">{EVENT_LABEL[l.event] || l.event}</td>
                  <td className="px-3 py-2 font-body truncate max-w-[200px]" title={l.google_email || l.app_user_id || ""}>
                    {l.google_email || l.app_user_id || "—"}
                  </td>
                  <td className="px-3 py-2 font-body">{l.app_role || "—"}</td>
                  <td className="px-3 py-2 font-body text-[#50493b]">{l.ip || "—"}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`subtitle uppercase tracking-[0.15em] text-[10px] px-2 py-0.5 border ${
                        l.success
                          ? "border-[#2d5e1f]/40 bg-[#e6f1df] text-[#1f4715]"
                          : "border-[#b30035]/40 bg-[#fbe4ea] text-[#7a0024]"
                      }`}
                    >
                      {l.success ? "OK" : "Fail"}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
        <div className="text-[11px] subtitle uppercase tracking-[0.15em] text-[#50493b]">
          {total} event{total !== 1 ? "s" : ""}
          {total > 0 ? ` · page ${data?.page || 1} of ${pages}` : ""}
        </div>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            data-testid="access-log-prev"
            disabled={loading || (data?.page || 1) <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="h-9 px-3 border-[#33424f]/30 text-[#33424f] text-xs disabled:opacity-40"
          >
            <ChevronLeft className="w-4 h-4 mr-1" /> Prev
          </Button>
          <Button
            type="button"
            variant="outline"
            data-testid="access-log-next"
            disabled={loading || (data?.page || 1) >= pages}
            onClick={() => setPage((p) => Math.min(pages, p + 1))}
            className="h-9 px-3 border-[#33424f]/30 text-[#33424f] text-xs disabled:opacity-40"
          >
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
